import { Megaphone, X } from "lucide-react";
import clsx from "clsx";

export type BannerTone = "info" | "warning" | "danger" | "brand";

export type BannerProps = {
  children: React.ReactNode;
  tone?: BannerTone;
  icon?: React.ReactNode;
  onDismiss?: () => void;
  action?: React.ReactNode;
  className?: string;
  sticky?: boolean;
};

const toneStyles: Record<BannerTone, string> = {
  info:    "bg-[var(--color-extended-blue-50)]/12 text-[var(--color-text-primary)] border-[var(--color-extended-blue-50)]/30",
  warning: "bg-[var(--color-warning-500)]/15 text-[var(--color-text-primary)] border-[var(--color-warning-500)]/40",
  danger:  "bg-[var(--color-danger-500)]/12 text-[var(--color-text-primary)] border-[var(--color-danger-500)]/30",
  brand:   "bg-[var(--color-accent)] text-[var(--color-text-inverse)] border-[var(--color-accent)]",
};

export function Banner({ children, tone = "info", icon, onDismiss, action, className, sticky }: BannerProps) {
  return (
    <div
      // Severity-correct live-region semantics: danger (incl. the maintenance lockdown banner,
      // which renders tone="danger") announces assertively as an alert; info/warning/brand stay
      // polite status messages.
      role={tone === "danger" ? "alert" : "status"}
      className={clsx(
        "w-full flex items-center gap-3 px-4 py-2.5 text-sm border-b",
        toneStyles[tone],
        sticky && "sticky top-0 z-30",
        className,
      )}
    >
      <span className="shrink-0">{icon ?? <Megaphone size={16} />}</span>
      <span className="flex-1">{children}</span>
      {action && <span className="shrink-0">{action}</span>}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss banner"
          className="shrink-0 opacity-70 hover:opacity-100 transition-opacity"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
